
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDateYMD } from '@/utils/dateFormat';
import { Globe, MapPin, Phone, Calendar, Clock, MessageSquare, ChevronRight, Pencil } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Company } from '@/lib/types';

interface CompanyDetailsInfoProps {
  company: Company;
}

const CompanyDetailsInfo = ({ company }: CompanyDetailsInfoProps) => {
  const navigate = useNavigate();
  const comp = company as any;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Company Information</CardTitle>
        <CardDescription>Basic details about {company.name}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {company.industry && (
          <div>
            <h3 className="text-sm font-medium">Industry</h3>
            <p>{company.industry}</p>
          </div>
        )}
        
        {company.size && (
          <div>
            <h3 className="text-sm font-medium">Company Size</h3>
            <Badge variant="outline">{company.size}</Badge>
          </div>
        )}
        
        {company.website && (
          <div>
            <h3 className="text-sm font-medium">Website</h3>
            <a 
              href={company.website} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-500 hover:underline flex items-center"
            >
              <Globe className="h-4 w-4 mr-2" />
              {company.website}
            </a>
          </div>
        )}

        {(comp.address || comp.city || comp.country) && (
          <div>
            <h3 className="text-sm font-medium flex items-center"><MapPin className="h-4 w-4 mr-1" /> Location</h3>
            <p>{[comp.address, comp.city, comp.country].filter(Boolean).join(', ')}</p>
          </div>
        )}

        {comp.phone && (
          <div>
            <h3 className="text-sm font-medium flex items-center"><Phone className="h-4 w-4 mr-1" /> Phone</h3>
            <p>{comp.phone}</p>
          </div>
        )}

        <div className="border-t pt-4 mt-4">
          <h3 className="text-sm font-medium mb-2">Localisation</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Currency:</span> {company.currency}
            </div>
            <div>
              <span className="text-muted-foreground">Date Format:</span> {comp.date_format || 'YYYY-MM-DD'}
            </div>
            {comp.timezone && (
              <div>
                <span className="text-muted-foreground">Timezone:</span> {comp.timezone}
              </div>
            )}
            {comp.locale && (
              <div>
                <span className="text-muted-foreground">Locale:</span> {comp.locale}
              </div>
            )}
          </div>
        </div>

        {comp.outreach_instructions && (
          <div className="border-t pt-4 mt-4">
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-1 text-sm font-medium w-full hover:text-foreground/80 transition-colors [&[data-state=open]>svg.chevron]:rotate-90">
                <MessageSquare className="h-4 w-4" />
                AI Outreach Instructions
                <ChevronRight className="chevron h-4 w-4 ml-auto transition-transform duration-200" />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="relative bg-muted/50 rounded-lg p-3 mt-2 text-sm whitespace-pre-wrap text-foreground/80">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-1 right-1 h-7 w-7"
                    onClick={() => navigate(`/companies/${company.id}/edit`)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {comp.outreach_instructions}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
        
        <div>
          <h3 className="text-sm font-medium">Created</h3>
          <p>{formatDateYMD(company.created_at)}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyDetailsInfo;
